import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import variantFilterRelevance from '@/utils/filtering/variantFilterRelevance';
import formatName from '@/utils/format/formatName';
import { AlertCircle, Sparkles } from '@/utils/lucide';

import { RootState } from '@/redux/store';

import LoadIndicator from '../Loader';
import { Button } from '../ui/button';
import { PrecomputedValueSet } from './NestedSelectDropdownPC';

type NoResultCheck = {
	set: any[];
	name: string;
	key: string;
	callback?: (filter: string) => void;
};

const useEnumCrossSetFallback = () =>
{
	// indications <> therapeutic areas
	// mechanisms <> targets <> product_type <> product_sub_type <> product_sub_type_detail
	// drugs_classes <> product_type <> product_sub_type <> product_sub_type_detail

	const setKeySets = [
		['indications', 'therapeutic_areas'],
		['mechanisms', 'targets'], // 'product_type', 'product_sub_type', 'product_sub_type_detail'],
		['drug_classes', 'product_types'] // 'product_sub_type', 'product_sub_type_detail']
	];

	//Generate and return these sets
	const enums = useSelector((state: RootState) => state.enums.enums);

	const sets = useMemo(() =>
	{
		return setKeySets.map(setKeys =>
		{
			return setKeys.map(setKey =>
			{
				const set: NoResultCheck = {
					key: setKey,
					name: formatName(setKey),
					set: (Array.isArray(enums[setKey])
						? enums[setKey]
						: ((enums[setKey] as PrecomputedValueSet)?.sortedDistinctValuesWithVariants ?? [])) as any[]
				};

				return set;
			});
		});
	}, [enums]);

	const getSetExcept: (key: string) => NoResultCheck[] = useCallback(
		(key: string) =>
		{
			//Find the first set of sets that contains the key, return the rest of the sets in that set.
			for (const set of sets)
			{
				if (set.some(set => set.key === key))
				{
					return set.filter(set => set.key !== key);
				}
			}

			return [];
		},
		[sets]
	);

	return {
		sets,
		getSetExcept
	};
};

const NoResultsFallback = ({
	filter,
	setFilter,
	check,
	callback,
	setName,
	suggestionDetails
}: {
	filter: string;
	check: NoResultCheck[];
	callback?: (filter: string, checkSet: NoResultCheck) => void;
	setFilter?: (filter: string) => void;
	setName?: string;
	suggestionDetails: {
		search_type?: string;
		enum?: string;
	};
}) =>
{
	//Check the filter against the other possible options.
	const found = useMemo(() =>
	{
		console.log('filter', filter, 'checking against', check.length, 'sets', check);

		const result = [];

		for (const checkSet of check)
		{
			let count = 0;
			let first = null;
			let firstRelevance = 0;

			for (const checkItem of checkSet.set)
			{
				const relevance = variantFilterRelevance(checkItem, filter);

				if (relevance > 0)
				{
					count++;

					if (count === 1 && !first)
					{
						first = checkItem.name;
						firstRelevance = relevance;
					}
					else if (relevance > firstRelevance)
					{
						first = checkItem.name;
						firstRelevance = relevance;
					}
				}
			}

			if (count > 0)
			{
				//Add the result to the result array.
				result.push({
					...checkSet,
					count,
					first
				});
			}
		}

		return result;
	}, [filter, check]);

	const [suggesting, setSuggesting] = useState(false);
	const [suggestions, setSuggestions] = useState<any[]>([]);
	const [showSuggestionWarning, setShowSuggestionWarning] = useState(false);
	useEffect(() =>
	{
		setSuggestions([]);
		setSuggesting(false);

		socket.on('suggest_alternative_terms', (data: { suggested_terms: any[]; enum: string }) =>
		{
			if (data.enum !== suggestionDetails.enum)
			{
				setSuggesting(false);
				return;
			}

			if (!data || !data.suggested_terms || data.suggested_terms.length === 0)
			{
				setSuggestions([]);
				setShowSuggestionWarning(true);
				return;
			}

			setSuggestions(data.suggested_terms);
		});

		return () =>
		{
			socket.off('suggest_alternative_terms');
		};
	}, [suggestionDetails?.enum]);

	return (
		<div className='flex flex-col gap-2 pt-2'>
			{found && (
				<>
					{found.map(checkSet => (
						<div
							key={checkSet.name}
							className='flex flex-col gap-3 items-center justify-center border border-gray-200 rounded-md p-2 bg-gray-100 w-full'
						>
							<p className=''>Try Filtering by {checkSet.name}</p>

							<p className='text-sm text-gray-500 text-center'>
								We couldn't find that term{!!setName ? ` in ${setName}` : ''}, but there are {checkSet.count} related result
								{checkSet.count === 1 ? '' : 's'} under {checkSet.name}.
							</p>
							<Button
								variant='outline'
								size='sm'
								// className='text-xs text-gray-500 p-1 rounded-md hover:bg-gray-100 active:bg-gray-200 transition-all duration-300'
								onClick={() =>
								{
									//If the callback is provided, call it.
									if (callback)
									{
										callback(filter, checkSet);
										return;
									}

									//If the callback is provided, call it.
									if (checkSet.callback)
									{
										checkSet.callback(filter);
									}
								}}
							>
								Switch to {checkSet.name} Filter
							</Button>
						</div>
					))}
					<hr className='my-2' />
				</>
			)}
			{showSuggestionWarning ? (
				<div className='bg-maven-indicator-50 border border-maven-indicator-200 rounded-lg p-4 flex items-start gap-3'>
					<div className='mt-1'>
						<AlertCircle className='text-icon text-maven-indicator-500' />
					</div>
					<div className='flex-1'>
						<p className='text-maven-indicator-700 font-medium mb-1'>No alternative terms found</p>
						<p className='text-maven-indicator-600 text-sm mb-3'>We couldn't identify any high-quality alternatives for your provided term.</p>
						<Button
							size='sm'
							variant='outline'
							className='bg-white border-maven-indicator-300 text-maven-indicator-700 hover:bg-maven-indicator-100 hover:text-maven-indicator-800'
							onClick={() =>
								{
								setShowSuggestionWarning(false);
								setSuggesting(true);

								socket.emit('suggest_alternative_terms', {
									existing_term: filter,
									...suggestionDetails
								});
							}}
						>
							Try Again
						</Button>
					</div>
				</div>
			) : (
				<div className='w-full flex flex-col items-center justify-center gap-2'>
					{suggestions.length === 0 && (
						<Button
							variant='primary'
							size='sm'
							disabled={suggesting}
							onClick={() =>
								{
								setSuggesting(true);

								socket.emit('suggest_alternative_terms', {
									existing_term: filter,
									...suggestionDetails
								});
							}}
						>
							<Sparkles className='text-icon-sm' />
							Suggest Alternative Terms
							{suggesting && <LoadIndicator className='text-white' />}
						</Button>
					)}
					{suggestions.length > 0 && (
						<>
							{/* <p className='flex flex-row gap-2 items-center'>
							<span>
								<Sparkles className='text-icon-sm text-maven-primary-600' />{' '}
							</span>
							AI-suggested Alternatives
						</p> */}
							<p className='text-sm text-gray-500'>Based on your search, here are some alternative terms you might consider:</p>
							{suggestions.map(suggestion => (
								<Button
									key={suggestion.term}
									variant='outline'
									className='h-auto group w-full py-0 relative'
									onClick={() =>
										{
										if (setFilter)
											{
											setFilter(suggestion.term);
										}
									}}
								>
									<div className='flex flex-col gap-1 p-2 px-0 w-full  items-start text-start'>
										<p className='text-sm font-semibold'>{suggestion.term}</p>
										<p className='text-xs text-wrap text-gray-500'>
											<span className='font-semibold'>Rationale:</span> {suggestion.rationale}
										</p>
										<p className='opacity-0 group-hover:opacity-100 transition-all duration-200 absolute top-2 right-2 text-xs font-semibold'>
											Click to use
										</p>
									</div>
								</Button>
							))}
						</>
					)}
				</div>
			)}
		</div>
	);

	if (found)
	{
		return (
			<div className='flex flex-col gap-2 pt-4'>
				{found.map(checkSet => (
					<div
						key={checkSet.name}
						className='flex flex-col gap-1 items-center justify-center border border-gray-200 rounded-md p-2 bg-gray-100 w-full'
					>
						<p className=''>Try Filtering by {checkSet.name}</p>

						<p className='text-xs text-gray-500 text-center'>
							We couldn't find "{filter}"{!!setName ? ` in ${setName}` : ''}, but there are {checkSet.count} related results under {checkSet.name}
							.
						</p>
						<Button
							variant='primary'
							size='sm'
							// className='text-xs text-gray-500 p-1 rounded-md hover:bg-gray-100 active:bg-gray-200 transition-all duration-300'
							onClick={() =>
							{
								//If the callback is provided, call it.
								if (callback)
								{
									callback(filter, checkSet);
									return;
								}

								//If the callback is provided, call it.
								if (checkSet.callback)
								{
									checkSet.callback(filter);
								}
							}}
						>
							Switch to {checkSet.name}
						</Button>
					</div>
				))}
			</div>
		);
	}
	else
	{
		return <></>;
	}
};

export default NoResultsFallback;
export { useEnumCrossSetFallback };
export type { NoResultCheck };
